import { create } from "zustand";

import type { CapabilityKey } from "../constants/capability";
import type { LifecycleState } from "../types/subscription/subscription";

export type EntitlementsCapabilities = Record<CapabilityKey, boolean>;

export interface EntitlementsFoundingMember {
  status: string;
  activatedAt: string | null;
  priceLockUntil: string | null;
  includedAddOnSlugs: string[];
  badgeLabel: string;
}

export interface EntitlementsSnapshot {
  capabilities: EntitlementsCapabilities;
  foundingMember: EntitlementsFoundingMember | null;
  activeBranchCount: number;
  lifecycleState: LifecycleState;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelledAt: string | null;
  entitlementSource: "plan" | "trial" | "custom" | "benefit" | null;
  activeAddOnSlugs: string[];
}

interface EntitlementsStore extends EntitlementsSnapshot {
  isHydrated: boolean;
  isRefreshing: boolean;
  setInitial: (snapshot: EntitlementsSnapshot) => void;
  refresh: () => Promise<void>;
}

const EMPTY_CAPABILITIES: EntitlementsCapabilities = {
  multi_branch: false,
  file_sharing: false,
  parent_engagement: false,
  business_operations: false,
  whatsapp_extras: false,
  add_ons: false,
};

const EMPTY_LIFECYCLE: Pick<
  EntitlementsSnapshot,
  | "lifecycleState"
  | "subscriptionStatus"
  | "currentPeriodEnd"
  | "trialEndsAt"
  | "cancelledAt"
  | "entitlementSource"
  | "activeAddOnSlugs"
> = {
  lifecycleState: "inactive",
  subscriptionStatus: null,
  currentPeriodEnd: null,
  trialEndsAt: null,
  cancelledAt: null,
  entitlementSource: null,
  activeAddOnSlugs: [],
};

async function fetchSnapshot(): Promise<EntitlementsSnapshot | null> {
  try {
    const response = await fetch("/api/core/subscription", {
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!response.ok) return null;
    const json = (await response.json()) as {
      success?: boolean;
      data?: {
        capabilities?: Partial<EntitlementsCapabilities>;
        foundingMember?: EntitlementsFoundingMember | null;
        activeBranchCount?: number;
        lifecycleState?: LifecycleState;
        entitlements?: {
          source?: EntitlementsSnapshot["entitlementSource"];
          activeAddOnSlugs?: string[];
        };
        subscription?: {
          status?: string;
          currentPeriodEnd?: string;
          trialEndsAt?: string;
          cancelledAt?: string;
        } | null;
      };
    };
    if (!json.success || !json.data) return null;
    return {
      capabilities: { ...EMPTY_CAPABILITIES, ...(json.data.capabilities ?? {}) },
      foundingMember: json.data.foundingMember ?? null,
      activeBranchCount: json.data.activeBranchCount ?? 0,
      lifecycleState: json.data.lifecycleState ?? "inactive",
      subscriptionStatus: json.data.subscription?.status ?? null,
      currentPeriodEnd: json.data.subscription?.currentPeriodEnd ?? null,
      trialEndsAt: json.data.subscription?.trialEndsAt ?? null,
      cancelledAt: json.data.subscription?.cancelledAt ?? null,
      entitlementSource: json.data.entitlements?.source ?? null,
      activeAddOnSlugs: json.data.entitlements?.activeAddOnSlugs ?? [],
    };
  } catch {
    return null;
  }
}

export const useEntitlementsStore = create<EntitlementsStore>((set) => ({
  capabilities: EMPTY_CAPABILITIES,
  foundingMember: null,
  activeBranchCount: 0,
  ...EMPTY_LIFECYCLE,
  isHydrated: false,
  isRefreshing: false,
  setInitial: (snapshot) =>
    set({
      capabilities: { ...EMPTY_CAPABILITIES, ...snapshot.capabilities },
      foundingMember: snapshot.foundingMember,
      activeBranchCount: snapshot.activeBranchCount,
      lifecycleState: snapshot.lifecycleState,
      subscriptionStatus: snapshot.subscriptionStatus,
      currentPeriodEnd: snapshot.currentPeriodEnd,
      trialEndsAt: snapshot.trialEndsAt,
      cancelledAt: snapshot.cancelledAt,
      entitlementSource: snapshot.entitlementSource,
      activeAddOnSlugs: snapshot.activeAddOnSlugs,
      isHydrated: true,
    }),
  refresh: async () => {
    set({ isRefreshing: true });
    const snapshot = await fetchSnapshot();
    if (snapshot) {
      set({
        capabilities: { ...EMPTY_CAPABILITIES, ...snapshot.capabilities },
        foundingMember: snapshot.foundingMember,
        activeBranchCount: snapshot.activeBranchCount,
        lifecycleState: snapshot.lifecycleState,
        subscriptionStatus: snapshot.subscriptionStatus,
        currentPeriodEnd: snapshot.currentPeriodEnd,
        trialEndsAt: snapshot.trialEndsAt,
        cancelledAt: snapshot.cancelledAt,
        entitlementSource: snapshot.entitlementSource,
        activeAddOnSlugs: snapshot.activeAddOnSlugs,
        isHydrated: true,
        isRefreshing: false,
      });
    } else {
      set({ isRefreshing: false });
    }
  },
}));
